import React, { useState, useEffect } from 'react';
import { lambdaService } from '../../services/lambdaService';
import { S3File, S3Folder } from '../../types/s3Transfer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  Loader2,
  AlertCircle,
  Search,
  FileText,
  Folder,
  FolderOpen,
  ArrowUpDown,
  DownloadCloud,
  RefreshCw,
  ChevronRight,
  Home
} from 'lucide-react';
import { toast } from 'sonner';

type CloudProvider = 'aws' | 'gcp';

interface S3FileBrowserProps {
  onTransferComplete: () => void;
  provider?: CloudProvider;
}

type SortField = 'name' | 'size' | 'lastModified';
type SortDirection = 'asc' | 'desc';

export const S3FileBrowser: React.FC<S3FileBrowserProps> = ({ onTransferComplete, provider = 'aws' }) => {
  const [files, setFiles] = useState<S3File[]>([]);
  const [folders, setFolders] = useState<S3Folder[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [bucketName, setBucketName] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    loadFiles();
  }, [currentPath]);

  // Supported file extensions
  const SUPPORTED_EXTENSIONS = ['.csv', '.tsv', '.zip', '.parquet', '.xlsx', '.xls'];

  const isFileSupported = (filename: string): boolean => {
    const lowerFilename = filename.toLowerCase();
    return SUPPORTED_EXTENSIONS.some(ext => lowerFilename.endsWith(ext));
  };

  const isGcp = provider === 'gcp';

  const loadFiles = async (continuationToken?: string) => {
    setLoading(true);
    setError(null);

    try {
      const browseFn = isGcp
        ? lambdaService.browseGcpFiles.bind(lambdaService)
        : lambdaService.browseS3Files.bind(lambdaService);
      const result = await browseFn(
        undefined, // use saved bucket
        currentPath, // current folder path
        continuationToken,
        1000
      );

      if (result.success) {
        // Filter files to only show supported extensions
        const supportedFiles = (result.files || []).filter(file => isFileSupported(file.name));

        if (continuationToken) {
          setFiles(prev => [...prev, ...supportedFiles]);
        } else {
          setFiles(supportedFiles);
          setFolders(result.folders || []);
        }
        setHasMore(result.hasMore || false);
        setNextToken(result.nextToken);
        setBucketName(result.bucketName || '');
      } else {
        setError(result.error || 'Failed to load files');
      }
    } catch (err) {
      setError(`Error loading files: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folderPath: string) => {
    setCurrentPath(folderPath);
    setSelectedKeys(new Set());
    setSearchTerm('');
  };

  const navigateUp = () => {
    if (!currentPath) return;

    // Remove trailing slash and get parent path
    const pathParts = currentPath.replace(/\/$/, '').split('/');
    pathParts.pop();
    const parentPath = pathParts.length > 0 ? pathParts.join('/') + '/' : '';
    navigateToFolder(parentPath);
  };

  const navigateToRoot = () => {
    navigateToFolder('');
  };

  // Get breadcrumb parts
  const getBreadcrumbs = () => {
    if (!currentPath) return [];
    const parts = currentPath.replace(/\/$/, '').split('/');
    const breadcrumbs = [];
    let path = '';
    for (const part of parts) {
      path += part + '/';
      breadcrumbs.push({ name: part, path });
    }
    return breadcrumbs;
  };

  const handleSelectAll = () => {
    const filteredFiles = getFilteredFiles();
    const allKeys = new Set(filteredFiles.map(f => f.key));
    setSelectedKeys(allKeys);
  };

  const handleDeselectAll = () => {
    setSelectedKeys(new Set());
  };

  const handleToggleFile = (key: string) => {
    const newSelected = new Set(selectedKeys);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedKeys(newSelected);
  };

  const handleTransfer = async () => {
    if (selectedKeys.size === 0) return;

    setTransferring(true);
    setError(null);

    try {
      const fileKeys = Array.from(selectedKeys);
      const transferFn = isGcp
        ? lambdaService.transferGcpFiles.bind(lambdaService)
        : lambdaService.transferS3Files.bind(lambdaService);
      const result = await transferFn(fileKeys);

      if (result.success) {
        if (result.failed && result.failed > 0) {
          toast.warning(`Transfer completed with ${result.failed} failures. ${result.successful} files transferred.`);
        } else {
          toast.success(`Successfully transferred ${result.successful} file(s)!`);
        }

        setSelectedKeys(new Set());
        onTransferComplete();
      } else {
        setError(result.error || 'Transfer failed');
        toast.error(result.error || 'Transfer failed');
      }
    } catch (err) {
      setError(`Error transferring files: ${err}`);
      toast.error('An unexpected error occurred during transfer');
    } finally {
      setTransferring(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredFiles = (): S3File[] => {
    if (!searchTerm.trim()) return files;
    const term = searchTerm.toLowerCase();
    return files.filter(file =>
      file.name.toLowerCase().includes(term)
    );
  };

  const getFilteredFolders = (): S3Folder[] => {
    if (!searchTerm.trim()) return folders;
    const term = searchTerm.toLowerCase();
    return folders.filter(folder =>
      folder.name.toLowerCase().includes(term)
    );
  };

  const getSortedFiles = (filesToSort: S3File[]): S3File[] => {
    return [...filesToSort].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'lastModified':
          comparison = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const filteredFolders = getFilteredFolders();
  const filteredFiles = getSortedFiles(getFilteredFiles());
  const selectedCount = selectedKeys.size;
  const breadcrumbs = getBreadcrumbs();

  if (loading && files.length === 0 && folders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">{isGcp ? 'Loading files from GCP...' : 'Loading files from S3...'}</p>
      </div>
    );
  }

  if (error && files.length === 0 && folders.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Failed to Load Files</AlertTitle>
        <AlertDescription className="flex flex-col gap-4 mt-2">
          <p>{error}</p>
          <Button variant="outline" onClick={() => loadFiles()} className="w-fit">
            <RefreshCw className="mr-2 h-4 w-4" /> Try Again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader className="pb-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              Select Files to Transfer
              {bucketName && <span className="text-sm font-normal text-muted-foreground ml-2">
                from <span className="font-medium text-foreground">{bucketName}</span>
              </span>}
            </CardTitle>
            <CardDescription>
              Navigate folders and choose files to import into BigSplice
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => loadFiles()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-1 mt-4 text-sm overflow-x-auto pb-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 shrink-0"
            onClick={navigateToRoot}
            disabled={!currentPath}
          >
            <Home className="h-4 w-4" />
          </Button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 truncate max-w-[150px]"
                onClick={() => navigateToFolder(crumb.path)}
                disabled={index === breadcrumbs.length - 1}
              >
                {crumb.name}
              </Button>
            </React.Fragment>
          ))}
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={transferring || filteredFiles.length === 0}
          >
            Select All Files
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeselectAll}
            disabled={transferring || selectedCount === 0}
          >
            Deselect All
          </Button>
          <span className="text-sm text-muted-foreground ml-2">
            {filteredFolders.length} folder{filteredFolders.length !== 1 ? 's' : ''}, {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-1">
                    Name <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('size')}>
                  <div className="flex items-center gap-1">
                    Size <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('lastModified')}>
                  <div className="flex items-center gap-1">
                    Last Modified <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Folders first */}
              {filteredFolders.map((folder) => (
                <TableRow
                  key={folder.key}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigateToFolder(folder.key)}
                >
                  <TableCell></TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Folder className="h-4 w-4 text-amber-500" />
                      <span className="truncate max-w-[300px]" title={folder.name}>
                        {folder.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                </TableRow>
              ))}

              {/* Then files */}
              {filteredFiles.length === 0 && filteredFolders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                    {searchTerm ? 'No matching files or folders found.' : 'This folder is empty.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredFiles.map((file) => (
                  <TableRow
                    key={file.key}
                    data-state={selectedKeys.has(file.key) ? "selected" : undefined}
                    className="cursor-pointer"
                    onClick={() => handleToggleFile(file.key)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedKeys.has(file.key)}
                        onCheckedChange={() => handleToggleFile(file.key)}
                        disabled={transferring}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate max-w-[300px]" title={file.name}>
                          {file.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{file.sizeFormatted}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {new Date(file.lastModified).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {hasMore && (
          <div className="flex justify-center mt-4">
            <Button variant="outline" onClick={() => loadFiles(nextToken)} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Load More Files
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex items-center justify-between border-t bg-muted/50 px-6 py-4">
        <div className="text-sm text-muted-foreground">
          {selectedCount} file{selectedCount !== 1 ? 's' : ''} selected
        </div>
        <Button
          onClick={handleTransfer}
          disabled={transferring || selectedCount === 0}
          className="gap-2"
        >
          {transferring ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Transferring...
            </>
          ) : (
            <>
              <DownloadCloud className="h-4 w-4" />
              Transfer Selected
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
